import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Rocket, CreditCard, CheckCircle, BarChart3 } from 'lucide-react';
import ProductionReadinessChecker from '@/components/testing/ProductionReadinessChecker';
import LiveModePaymentTester from '@/components/testing/LiveModePaymentTester';
import EndToEndTesting from '@/components/testing/EndToEndTesting';

const ProductionTestingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Production Deployment Center
          </h1>
          <p className="text-gray-600">
            Comprehensive testing and validation for CareKonnect production launch
          </p>
        </div>

        <Tabs defaultValue="readiness" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="readiness" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Readiness Check
            </TabsTrigger>
            <TabsTrigger value="stripe" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Stripe Testing
            </TabsTrigger>
            <TabsTrigger value="end-to-end" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              E2E Testing
            </TabsTrigger>
            <TabsTrigger value="deployment" className="flex items-center gap-2">
              <Rocket className="h-4 w-4" />
              Deployment
            </TabsTrigger>
          </TabsList>

          <TabsContent value="readiness">
            <ProductionReadinessChecker />
          </TabsContent>

          <TabsContent value="stripe">
            <LiveModePaymentTester />
          </TabsContent>

          <TabsContent value="end-to-end">
            <EndToEndTesting />
          </TabsContent>

          <TabsContent value="deployment">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rocket className="h-6 w-6" />
                  Production Deployment Guide
                </CardTitle>
                <CardDescription>
                  Final steps for going live with CareKonnect
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">Pre-Deployment Checklist</h3>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li>✅ All production readiness checks pass</li>
                      <li>✅ Stripe live mode tested and validated</li>
                      <li>✅ End-to-end user journeys verified</li>
                      <li>✅ Security scan shows no critical issues</li>
                      <li>✅ Support email configured and tested</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h3 className="font-semibold text-yellow-900 mb-2">Domain & Infrastructure</h3>
                    <ul className="space-y-2 text-sm text-yellow-800">
                      <li>📋 Update Stripe webhook URLs to production domain</li>
                      <li>📋 Configure SSL certificates for custom domain</li>
                      <li>📋 Set up production monitoring and alerting</li>
                      <li>📋 Configure automated backups</li>
                      <li>📋 Set up error tracking and logging</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Go-Live Process</h3>
                    <ol className="space-y-2 text-sm text-green-800 list-decimal list-inside">
                      <li>Deploy to production environment</li>
                      <li>Update DNS records to point to production</li>
                      <li>Test critical flows with real users</li>
                      <li>Monitor error rates and performance</li>
                      <li>Set up customer onboarding process</li>
                      <li>Launch announcement and user communications</li>
                    </ol>
                  </div>

                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-2">Post-Launch Monitoring</h3>
                    <ul className="space-y-2 text-sm text-purple-800">
                      <li>Monitor user registration and subscription rates</li>
                      <li>Track payment success/failure rates</li>
                      <li>Monitor email delivery and notification systems</li>
                      <li>Watch for error spikes or performance issues</li>
                      <li>Collect user feedback and iterate quickly</li>
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Key Metrics to Track</h4>
                    <ul className="text-sm space-y-1">
                      <li>• User registrations per day</li>
                      <li>• Subscription conversion rate</li>
                      <li>• Referral submission volume</li>
                      <li>• Payment success rate</li>
                      <li>• System uptime and response times</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Emergency Contacts</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Technical Support: Your team</li>
                      <li>• Stripe Support: dashboard.stripe.com</li>
                      <li>• Supabase Support: supabase.com/support</li>
                      <li>• Customer Support: Your support email</li>
                    </ul>
                  </div>
                </div>

                <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    🚀 Ready for Launch!
                  </h3>
                  <p className="text-gray-600">
                    CareKonnect is technically ready for production deployment. 
                    Follow the checklist above and you'll be live soon!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProductionTestingPage;